import { useState } from 'react'
import { toast } from 'sonner'
import { Button, Drawer, Select } from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import {
  useAcceptDevItemProposal,
  useDevItem,
  useDismissDevItemProposal,
  useTransitionDevItem,
} from '../api/queries'
import type { DevEvent, DevSessionSummary } from '../api/types'
import { ALL_ITEM_STATES, stateLabel } from '../utils/state-meta'
import { StateBadge } from './StateBadge'

export interface DevItemDrawerProps {
  itemRef: string | null
  onClose: () => void
}

interface WipConflict {
  limit: number
  active: string[]
  hint: string
}

interface HotfixConflict {
  reservePct: number
  hint: string
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className="w-28 flex-shrink-0 text-xs font-medium uppercase text-tertiary">
        {label}
      </span>
      <span className="min-w-0 flex-1 text-sm text-primary">{value ?? '—'}</span>
    </div>
  )
}

function EventRow({ event }: { event: DevEvent }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border-subtle py-2 last:border-b-0">
      <div className="min-w-0">
        <div className="text-sm text-primary">{event.kind.replace(/_/g, ' ')}</div>
        <div className="text-xs text-tertiary">{event.actor}</div>
      </div>
      <div className="flex-shrink-0 text-xs text-secondary">{fmtDateTime(event.created_at)}</div>
    </div>
  )
}

function SessionRow({ session }: { session: DevSessionSummary }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border-subtle py-2 last:border-b-0">
      <div className="min-w-0">
        <div className="text-sm text-primary">
          {session.repo ?? 'unknown repo'}
          {session.branch ? <span className="text-tertiary"> · {session.branch}</span> : null}
        </div>
        <div className="text-xs text-tertiary">
          {session.status}
          {session.end_reason ? ` · ${session.end_reason}` : ''}
        </div>
      </div>
      <div className="flex-shrink-0 text-xs text-secondary">{fmtDateTime(session.ended_at)}</div>
    </div>
  )
}

/** Extracts the structured 409 payload transition_dev_item raises
 * (see app/api/routes/dev_tracker.py) — falls back to a generic toast
 * when the error isn't one of the two policy conflicts this drawer
 * knows how to offer a resolution for. */
function describeError(error: unknown): string {
  if (error instanceof ApiError && typeof error.detail === 'object' && error.detail) {
    const detail = error.detail as { error?: string; hint?: string }
    if (detail.hint) return `${detail.error ?? 'Request failed'} — ${detail.hint}`
  }
  return error instanceof Error ? error.message : 'Something went wrong.'
}

/**
 * Owns the "move to" picker + WIP/hotfix conflict resolution for ONE
 * item. Rendered with `key={itemRef}` by the parent so switching items
 * gets a fresh instance (and therefore fresh local state) via a plain
 * remount — no effect needed to sync local state to a changing prop,
 * which is exactly the pattern React's own docs recommend over
 * useEffect+setState for "reset state when an id changes."
 */
function MoveToPanel({ itemRef, currentState }: { itemRef: string; currentState: string }) {
  const transition = useTransitionDevItem(itemRef)
  const [moveToState, setMoveToState] = useState(currentState)
  const [wipConflict, setWipConflict] = useState<WipConflict | null>(null)
  const [hotfixConflict, setHotfixConflict] = useState<HotfixConflict | null>(null)
  const [displacesRef, setDisplacesRef] = useState('')

  const runTransition = (overrides: { displacesRef?: string; hotfixOverride?: boolean } = {}) => {
    setWipConflict(null)
    setHotfixConflict(null)
    transition.mutate(
      {
        toState: moveToState,
        displacesRef: overrides.displacesRef,
        hotfixOverride: overrides.hotfixOverride,
      },
      {
        onSuccess: () => toast.success(`${itemRef} → ${stateLabel(moveToState)}`),
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409 && typeof error.detail === 'object' && error.detail) {
            const detail = error.detail as {
              error?: string; limit?: number; active?: string[]; hint?: string; reserve_pct?: number
            }
            if (detail.error === 'wip_limit_reached') {
              setWipConflict({ limit: detail.limit ?? 0, active: detail.active ?? [], hint: detail.hint ?? '' })
              setDisplacesRef(detail.active?.[0] ?? '')
              return
            }
            if (detail.error === 'hotfix_reserve_exceeded') {
              setHotfixConflict({ reservePct: detail.reserve_pct ?? 0, hint: detail.hint ?? '' })
              return
            }
          }
          toast.error(describeError(error))
        },
      },
    )
  }

  return (
    <section>
      <h3 className="mb-1.5 text-xs font-medium uppercase text-tertiary">Move to</h3>
      <div className="flex items-center gap-2">
        <Select
          aria-label="Move to state"
          value={moveToState}
          onChange={(e) => setMoveToState(e.target.value)}
        >
          {ALL_ITEM_STATES.map((s) => (
            <option key={s} value={s}>
              {stateLabel(s)}
            </option>
          ))}
        </Select>
        <Button
          variant="secondary"
          size="sm"
          disabled={moveToState === currentState}
          loading={transition.isPending}
          onClick={() => runTransition()}
        >
          Move
        </Button>
      </div>

      {wipConflict ? (
        <div className="mt-2 rounded-sm border border-warning-border bg-warning-bg p-3 text-sm">
          <p className="text-primary">
            WIP limit ({wipConflict.limit}) reached — active: {wipConflict.active.join(', ')}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Select
              aria-label="Displace"
              value={displacesRef}
              onChange={(e) => setDisplacesRef(e.target.value)}
            >
              {wipConflict.active.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
            <Button
              variant="secondary"
              size="sm"
              loading={transition.isPending}
              onClick={() => runTransition({ displacesRef })}
            >
              Defer {displacesRef} and move
            </Button>
          </div>
        </div>
      ) : null}

      {hotfixConflict ? (
        <div className="mt-2 rounded-sm border border-warning-border bg-warning-bg p-3 text-sm">
          <p className="text-primary">
            Hotfix reserve ({hotfixConflict.reservePct}%) exceeded for this stream.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-2"
            loading={transition.isPending}
            onClick={() => runTransition({ hotfixOverride: true })}
          >
            Override and move anyway
          </Button>
        </div>
      ) : null}
    </section>
  )
}

/**
 * Detail drawer with Phase 4's transition/accept/dismiss actions. No
 * drag-and-drop kanban here on purpose (see module.config.ts) — a plain
 * "move to" picker plus explicit WIP/hotfix conflict resolution is the
 * honest fit for policy that can reject a move with a reason.
 */
export function DevItemDrawer({ itemRef, onClose }: DevItemDrawerProps) {
  const { data, isLoading, isError, refetch } = useDevItem(itemRef)
  const acceptProposal = useAcceptDevItemProposal(itemRef)
  const dismissProposal = useDismissDevItemProposal(itemRef)

  return (
    <Drawer
      open={itemRef !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      ident={data?.ref ?? itemRef ?? undefined}
      title={data?.title ?? 'Loading…'}
      meta={
        data ? (
          <>
            <StateBadge state={data.state} />
            <span className="opacity-40">·</span>
            <span>{data.stream}</span>
          </>
        ) : undefined
      }
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {itemRef === null ? null : isLoading ? (
        <div className="p-6 text-sm text-tertiary">Loading…</div>
      ) : isError ? (
        <div className="flex flex-col gap-2 p-6">
          <p className="text-sm text-danger-text">Failed to load this item.</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : data ? (
        <div className="flex flex-col gap-6 p-5">
          {data.proposed_state ? (
            <section className="rounded-sm border border-info-border bg-info-bg p-3">
              <div className="mb-2 text-sm text-primary">
                CARLA proposed moving this to{' '}
                <span className="font-medium">{stateLabel(data.proposed_state)}</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  loading={acceptProposal.isPending}
                  onClick={() =>
                    acceptProposal.mutate(undefined, {
                      onSuccess: () => toast.success(`${itemRef} → ${stateLabel(data.proposed_state!)}`),
                      onError: (error) => toast.error(describeError(error)),
                    })
                  }
                >
                  Accept
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={dismissProposal.isPending}
                  onClick={() =>
                    dismissProposal.mutate(undefined, {
                      onError: (error) => toast.error(describeError(error)),
                    })
                  }
                >
                  Dismiss
                </Button>
              </div>
            </section>
          ) : null}

          <MoveToPanel key={itemRef} itemRef={itemRef} currentState={data.state} />

          <section>
            <FieldRow label="Detail" value={data.detail} />
            <FieldRow label="Next action" value={data.next_action} />
            <FieldRow label="Owner" value={data.action_owner} />
            <FieldRow label="Work type" value={data.work_type} />
            {data.blocked_reason ? <FieldRow label="Blocked" value={data.blocked_reason} /> : null}
            {data.deferred_reason ? (
              <FieldRow label="Deferred" value={data.deferred_reason} />
            ) : null}
            <FieldRow label="Created" value={fmtDateTime(data.created_at)} />
          </section>

          <section>
            <h3 className="mb-1 text-xs font-medium uppercase text-tertiary">
              Recent events {data.events.length ? `(${data.events.length})` : ''}
            </h3>
            {data.events.length === 0 ? (
              <p className="text-sm text-tertiary">No events recorded yet.</p>
            ) : (
              data.events.map((e) => <EventRow key={e.id} event={e} />)
            )}
          </section>

          <section>
            <h3 className="mb-1 text-xs font-medium uppercase text-tertiary">
              Recent sessions {data.sessions.length ? `(${data.sessions.length})` : ''}
            </h3>
            {data.sessions.length === 0 ? (
              <p className="text-sm text-tertiary">
                No Claude Code sessions have reported against this item yet.
              </p>
            ) : (
              data.sessions.map((s) => <SessionRow key={s.id} session={s} />)
            )}
          </section>
        </div>
      ) : null}
    </Drawer>
  )
}
